import { AmountInfoBoxLocalized, InfoBoxBadgeLabelWithTooltip, Translate } from '@components'

import { AmountInfoBoxesGroup, AmountInfoBoxProps, Space, Text } from '@billy/design-system'
import { ReactElement, ReactNode, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { invoiceStatusToBadgeColor } from '@features/invoices/constants/invoiceStatusToBadgeVariant'
import { invoiceStatusToTitleKey } from '@features/invoices/constants/invoiceStatusToTitleKey'
import { InvoiceStatus } from '@features/invoices/enums/invoiceStatus'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useSegment } from '../../../../../../hooks'
import { useInvoicesListFilters } from '../../hooks/useInvoicesListFilters'
import { useInvoicesSummaryFiltered } from '../../hooks/useInvoicesSummaryFiltered'
import * as Styled from './styles'

const SUMMARY_BOXES_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.All,
  InvoiceStatus.Draft,
  InvoiceStatus.Overdue,
  InvoiceStatus.Unpaid,
  InvoiceStatus.Paid,
  InvoiceStatus.Deleted,
]

type SummaryInfoBox = Pick<
  AmountInfoBoxProps<InvoiceStatus>,
  'active' | 'amount' | 'badgeAmount' | 'badgeLabel' | 'badgeColor' | 'id'
>

export const InvoicesSummary = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { data, isLoading } = useInvoicesSummaryFiltered()
  const [{ status: currentStatus }, setFilters] = useInvoicesListFilters()

  const handleClick = useCallback(
    (id: InvoiceStatus) => {
      setFilters({ page: 1, status: id })
      track('XXX - Invoicing Core - Invoices List - Summary box clicked', { option: id })
    },
    [setFilters, track],
  )

  const infoBoxes: SummaryInfoBox[] = useMemo(() => {
    const infoBoxesData = SUMMARY_BOXES_STATUSES.map((status) => {
      const { amount: dataAmount, count: dataCount } = data?.[status] || {}
      const badgeAmount = dataCount
      const amount = dataAmount
      const label = t(invoiceStatusToTitleKey[status], { count: badgeAmount })
      const tooltip = t('external_invoices.category.all.tooltip')

      const badgeLabel: ReactNode =
        status === InvoiceStatus.All ? <InfoBoxBadgeLabelWithTooltip label={label} tooltipLabel={tooltip} /> : label

      return {
        active: status === currentStatus,
        amount,
        badgeAmount,
        badgeLabel,
        badgeColor: invoiceStatusToBadgeColor[status],
        id: status,
      }
    })

    // We want to hide the "Deleted" status (Cancelled) section when there are no soft-deleted
    // invoices, as it doesn't bring value to the user and can be confusing

    return infoBoxesData.filter(({ amount, id }) => !(id === InvoiceStatus.Deleted && amount === 0))
  }, [currentStatus, data, t])

  return (
    <Styled.InvoicesSummaryWrapper>
      <Text variant="h4">
        <Translate value="bills.summary.title" />
      </Text>
      <Space size="s16" />
      <AmountInfoBoxesGroup itemsInRow={infoBoxes.length} loading={isLoading}>
        {infoBoxes.map(({ active, amount, badgeAmount, badgeLabel, badgeColor, id }) => (
          <AmountInfoBoxLocalized
            active={active}
            amount={amount}
            badgeAmount={badgeAmount}
            badgeLabel={badgeLabel}
            badgeColor={badgeColor}
            clickable
            currencyId={organization?.baseCurrencyId}
            id={id}
            key={id}
            onClick={handleClick}
            shortFormat={false}
          />
        ))}
      </AmountInfoBoxesGroup>
    </Styled.InvoicesSummaryWrapper>
  )
}
